import ChartDataLabels from 'chartjs-plugin-datalabels'

const ageRgbNumber = '106,159,58'
export const trendColors = ['#98CCC4', '#FFDF7D', 'rgba(197,90,17,1)']
export const channelColors = ['#4472C4', '#ED7D31', '#FFC000', '#F44F39', '#E27C7F']
export const channelShareColors = [
  '#FABFBD', '#F8CBAD', '#FFE699', '#C5E0B4', '#BDD7EE', '#DCD4F4',
  '#FABFBD', '#F8CBAD', '#FFE699', '#C5E0B4', '#BDD7EE', '#DCD4F4',
  '#FABFBD', '#F8CBAD', '#FFE699', '#C5E0B4', '#BDD7EE', '#DCD4F4',
  '#FABFBD', '#F8CBAD', '#FFE699', '#C5E0B4', '#BDD7EE', '#DCD4F4'
]

const roundToUnit = value => {
  if (value < 10) return 0
  const exponent = Math.floor(Math.log10(value))
  const base = (10 ** exponent) / 2
  return base
}

const defProps = ({ usePercentage, labelAlignCenter, scales, height = 160, legend, extras, tooltips = {}, dataLabelFormatter, hideDataLabel, padding = {} }) => ({
  options: {
    plugins: {
      datalabels: {
        display: !hideDataLabel,
        anchor: labelAlignCenter ? 'center' : 'end',
        align: labelAlignCenter ? 'center' : 'end',
        formatter: (value, chart) => {
          const val = dataLabelFormatter ? dataLabelFormatter(value, chart) : value
          if (!val) return null
          return usePercentage ? `${val}%` : Number(val)?.toLocaleString()
        }
      }
    },
    legend: legend || { display: false },
    interaction: { mode: 'index', intersect: true },
    tooltips: {
      mode: 'x-axis',
      intersect: true,
      displayColors: false,
      callbacks: {
        label: i => {
          return usePercentage ? `${i?.value}%` : Number(i?.value || 0)?.toLocaleString()
        }
      },
      ...tooltips
    },
    scales,
    layout: { padding }
  },
  plugins: [ChartDataLabels],
  height,
  ...extras
})

export const genderChartProps = ({ data, ln_t }) => ({
  data: {
    datasets: [{
      data: ['female', 'male', 'other'].map(i => data?.[i]?.percentage),
      backgroundColor: ['#F66451', '#5B9BD5', '#D9D9D9'],
      hoverOffset: 4
    }],
    labels: [ln_t('invoice.female'), ln_t('invoice.male'), ln_t('invoice.other')]
  },
  ...defProps({
    usePercentage: true,
    scales: {
      yAxes: [
        {
          ticks: {
            suggestedMin: 0,
            suggestedMax: Math.max(...Object.values(data)?.map(i => i.percentage)) + 5
          }
        }
      ]
    }
  })
})

export const ageChartProps = ({ data }) => {
  const cleanKey = key => key?.split('-')?.[0]?.replace('+', '')
  const sorted = Object.entries(data).sort(([key1], [key2]) => cleanKey(key1) - cleanKey(key2))
  return ({
    data: {
      datasets: [{
        data: sorted?.map(([_, i]) => i.percentage),
        backgroundColor: sorted?.map(([_, i]) => `rgba(${ageRgbNumber}, ${i.percentage > 20 ? 1 : 0.5})`),
        hoverOffset: 4
      }],
      labels: sorted?.map(([key]) => key)
    },
    ...defProps({
      usePercentage: true,
      tooltips: { mode: 'index' }
    })
  })
}

export const trendChartProps = ({ data, ln_t, chartState }) => ({
  data: {
    datasets: [
      {
        label: ln_t('invoice.quantity'),
        data: data?.map(i => i.inv_num),
        backgroundColor: trendColors[0],
        hoverOffset: 4,
        order: 1,
        yAxisID: 'left',
        key: 'inv_num'
      },
      {
        label: ln_t('invoice.transactions'),
        data: data?.map(i => i.uuid),
        backgroundColor: trendColors[1],
        hoverOffset: 4,
        order: 1,
        yAxisID: 'left',
        key: 'uuid'
      },
      {
        label: ln_t('invoice.totalConsumers'),
        data: data?.map(i => i.amount),
        borderColor: trendColors[2],
        backgroundColor: 'transparent',
        type: 'line',
        order: 0,
        yAxisID: 'right',
        tension: 0,
        key: 'amount'
      }
    ],
    labels: data?.map(i => i.date)
  },
  ...defProps({
    padding: { top: 16 },
    dataLabelFormatter: (val, chart) => {
      const highlight = chartState[chart?.dataset?.key] || []
      const show = highlight?.includes(Number(val))
      return show ? val : undefined
    },
    tooltips: { displayColors: true },
    scales: {
      yAxes: [
        {
          id: 'left',
          type: 'linear',
          position: 'left',
          ticks: {
            suggestedMin: 0,
            suggestedMax: Math.max(...data?.map(i => [i.inv_num, i.uuid])?.flat()) + roundToUnit(Math.max(...data?.map(i => [i.inv_num, i.uuid])?.flat())),
            callback: value => value?.toLocaleString()
          }
        },
        {
          id: 'right',
          type: 'linear',
          position: 'right',
          gridLines: { display: false },
          ticks: {
            suggestedMax: Math.max(...data?.map(i => [i.inv_num, i.uuid])?.flat()) + roundToUnit(Math.max(...data?.map(i => [i.inv_num, i.uuid])?.flat())),
            callback: value => value?.toLocaleString()
          }
        }
      ]
    },
    height: 100
  })
})

export const channelLabel = data => {
  const removeDate = Object.keys(data?.[0] || {})?.filter(i => i !== 'date')
  return [...removeDate.filter(item => item !== '其他'), ...removeDate.filter(item => item === '其他')]
}
export const channelChartProps = ({ data, chartState }) => ({
  data: {
    datasets: channelLabel(data)?.filter(i => chartState?.selected?.includes(i)).map(channel => ({
      label: channel,
      data: data?.map(i => i?.[channel]),
      borderColor: chartState?.colorMapping?.[channel],
      backgroundColor: 'transparent',
      tension: 0
    })),
    labels: data?.map(i => i.date)
  },
  ...defProps({
    padding: { top: 16, right: 24 },
    tooltips: {
      xAlign: 'center',
      yAlign: 'bottom',
      caretPadding: 12,
      displayColors: true,
      callbacks: {
        labelColor: (i, chart) => {
          const channel = chart?.config?.data?.datasets?.[i.datasetIndex]?.label
          return {
            borderColor: 'transparent', // outline
            backgroundColor: chartState?.colorMapping?.[channel] // icon fill color
          }
        },
        label: (i, chart) => {
          const channel = chart?.datasets?.[i.datasetIndex]?.label
          return Number(i.value) ? `${channel}: ${Number(i?.value)?.toLocaleString()}` : null
        }
      }
    },
    dataLabelFormatter: val => Number(val) === chartState?.highest ? val : undefined,
    scales: {
      yAxes: [{
        ticks: {
          suggestedMax: Math.max(...channelLabel(data)?.map(channel => data?.map(i => i?.[channel]))?.flat()) + roundToUnit(Math.max(...channelLabel(data)?.map(channel => data?.map(i => i?.[channel]))?.flat())),
          callback: value => value?.toLocaleString()
        }
      }]
    },
    height: 120
  })
})

export const channelShareChartProps = ({ data }) => ({
  data: {
    datasets: [{
      data: Object.values(data)?.map(i => i?.percentage),
      backgroundColor: channelShareColors
    }],
    labels: Object.keys(data)
  },
  ...defProps({
    height: 100,
    labelAlignCenter: true,
    usePercentage: true,
    dataLabelFormatter: val => val < 5 ? undefined : val,
    legend: {
      display: true,
      position: 'right',
      labels: {
        generateLabels: chart => {
          const data = chart?.config?.data?.datasets?.[0]?.data || []
          const color = chart?.config?.data?.datasets?.[0]?.backgroundColor || []
          return chart?.data?.labels?.map((label, idx) => ({
            datasetIndex: idx,
            text: `${label} (${data[idx]}%)`,
            fillStyle: color[idx],
            strokeStyle: color[idx],
            lineWidth: 0,
            hidden: chart.getDatasetMeta(0).hidden
          }))
        },
        usePointStyle: true,
        pointStyle: 'circle',
        boxWidth: 5
      }
    },
    tooltips: {
      callbacks: {
        label: (i, chart) => {
          const data = chart?.datasets?.[0]?.data
          return `${data[i?.index]}%`
        }
      }
    }
  })
})
